/*
 * The one shortcut registry (A31). Every binding in the application — core's and every module's,
 * through the `shortcuts` slot — is a `ShortcutDef` in here, which is why the sheet, the menu hints
 * and the tooltips can render *from* the registry: an undocumented shortcut cannot exist.
 *
 * Two rules are load-bearing and both live in `find`. A binding without a Ctrl/Alt/Meta modifier
 * whose key is a single character never fires while a text field or a contenteditable has focus —
 * otherwise typing "j" in the composer would jump to the next message. And a binding only fires
 * while its scope is on the scope stack, so `↑` means "edit my last message" in the composer and
 * nothing at all anywhere else.
 */

import { activityShortcuts } from "../activity/shortcuts.ts"
import { DOCUMENT_SCOPE_STACKS, documentShortcuts } from "../docs/shortcuts.ts"
import type { ShortcutDef, ShortcutScopeDef } from "../modules/registry.ts"

export type ShortcutScope = ShortcutDef["scope"]

/** Scopes that can be open at the same time, each list innermost last. */
export type ScopeStacks = readonly (readonly ShortcutScope[])[]

/**
 * Which of core's scopes can be open at the same time, innermost last — the ground every collision
 * question stands on. A conversation route pushes `conversation` and `messageList` for its whole
 * lifetime and the message box sits inside it, so those three and `global` are live together: a
 * chord the box claims is a chord the application must not claim as well (X-A4 D1, the `Ctrl+K`
 * defect). The Activity feed is a page of its own, which is why its `j`, `k` and `r` may mean there
 * what the timeline's mean here without that being a clash.
 *
 * A document page is core's and brings a stack for each of its surfaces (`core/docs/shortcuts.ts`).
 * A module's page — an AI chat, a review's code — brings its own stack with the scopes it declares
 * (`ShortcutScopeDef`, the `shortcutScopes` slot), and `scopeStacks` adds them.
 *
 * It is a list of stacks rather than a flag per scope because that is what a stack is: the question
 * is never "do these two scopes exist" but "can a reader have both of them open".
 */
export const CORE_SCOPE_STACKS: ScopeStacks = [
	["global", "conversation", "messageList", "composer"],
	["global", "activity"],
	...DOCUMENT_SCOPE_STACKS,
]

/** Core's stacks and every stack the modules' scopes live on, each once. */
export function scopeStacks(contributed: readonly ShortcutScopeDef[]): ScopeStacks {
	const stacks = [...CORE_SCOPE_STACKS]
	for (const { stack } of contributed) {
		if (!stacks.some((known) => known.join(" ") === stack.join(" "))) stacks.push(stack)
	}
	return stacks
}

/** True while two scopes can be on the stack at the same time, so one stroke reaches both. */
export function scopesOverlap(
	one: ShortcutScope,
	other: ShortcutScope,
	stacks: ScopeStacks,
): boolean {
	if (one === other) return true
	return stacks.some((stack) => stack.includes(one) && stack.includes(other))
}

/**
 * The parsed form of one key spec ("Mod+Shift+F"): the modifiers wanted, and the key itself.
 *
 * **Which key a chord names** (DV S-13-D9). A Latin letter is the letter the layout prints: `Mod+Z`
 * is undo on German QWERTZ too, where Z sits on the American Y — matching the position instead made
 * German Ctrl+Z fire whatever the American Ctrl+Y does. Only where the layout prints no Latin letter
 * there (Cyrillic, or what macOS's Option makes of a key) does the letter fall back to its position.
 * A digit is its position, because Shift+7 is `&` on one keyboard and `/` on another. A spec may
 * also name a position outright, as the DOM's code in brackets — `Mod+[BracketRight]`, `[KeyH]` —
 * which is how a punctuation chord says "this key" rather than "this character". A punctuation
 * character without brackets stays a character: the sheet opens on `?` and `/` wherever the
 * layout puts them.
 */
interface Chord {
	ctrl: boolean
	meta: boolean
	alt: boolean
	shift: boolean
	/** True while the spec said "Mod", which is Cmd on a Mac and Ctrl everywhere else. */
	mod: boolean
	/** The key, lowercased: a character or a named key ("enter", "f6"); empty for a code chord. */
	key: string
	/** The DOM `code` the spec named in brackets, lowercased ("bracketright"), or empty. */
	code: string
}

/**
 * The parts of a keyboard event `find` compares against; a real KeyboardEvent satisfies it. `code`
 * is the key's position; `getModifierState` tells AltGraph apart, which a plain object can say as
 * `altGraph` instead.
 */
export interface KeyStroke {
	key: string
	code?: string
	ctrlKey: boolean
	metaKey: boolean
	altKey: boolean
	shiftKey: boolean
	altGraph?: boolean
	getModifierState?(key: string): boolean
}

/** Specs read once: a key's chords are matched against the same few dozen strings on every stroke. */
const parsed = new Map<string, Chord>()

function parseChord(spec: string): Chord {
	let chord = parsed.get(spec)
	if (chord === undefined) {
		chord = readChord(spec)
		parsed.set(spec, chord)
	}
	return chord
}

function readChord(spec: string): Chord {
	const parts = spec.split("+").map((part) => part.trim().toLowerCase())
	const last = parts[parts.length - 1] ?? ""
	const modifiers = new Set(parts.slice(0, -1))
	const bracketed = /^\[(.+)\]$/.exec(last)
	return {
		ctrl: modifiers.has("ctrl") || modifiers.has("control"),
		meta: modifiers.has("meta") || modifiers.has("cmd"),
		alt: modifiers.has("alt") || modifiers.has("option"),
		shift: modifiers.has("shift"),
		mod: modifiers.has("mod"),
		key: bracketed ? "" : last,
		code: bracketed ? (bracketed[1] ?? "") : "",
	}
}

/**
 * The character each position carries on the American layout, which is what a code chord prints
 * where the browser cannot name the reader's own keycap (`keyLabels`), and what lets the collision
 * check see that `Mod+[Slash]` and `Mod+/` are one stroke there.
 */
const US_CHARACTERS: Record<string, string> = {
	backslash: "\\",
	bracketleft: "[",
	bracketright: "]",
	slash: "/",
	period: ".",
	comma: ",",
	minus: "-",
	equal: "=",
	semicolon: ";",
	quote: "'",
	backquote: "`",
}

/** The one stroke a chord's key stands for, by position where it has one: `[keyh]`, `[digit7]`. */
function positionOf(chord: Chord): string {
	if (chord.code !== "") return chord.code
	if (/^[a-z]$/.test(chord.key)) return `key${chord.key}`
	if (/^[0-9]$/.test(chord.key)) return `digit${chord.key}`
	const code = Object.entries(US_CHARACTERS).find(([, char]) => char === chord.key)?.[0]
	return code ?? chord.key
}

/** A canonical spelling of one spec, so two bindings that mean the same collide as the same. */
export function canonicalKeys(spec: string): string {
	const chord = parseChord(spec)
	const names = [
		chord.mod ? "mod" : "",
		chord.ctrl ? "ctrl" : "",
		chord.meta ? "meta" : "",
		chord.alt ? "alt" : "",
		chord.shift ? "shift" : "",
	].filter((name) => name !== "")
	return [...names, chord.code !== "" ? `[${chord.code}]` : chord.key].join("+")
}

/**
 * Every stroke one spec answers, one canonical string each, on **either** platform: "Mod+K" is
 * `ctrl+k` on a PC and `meta+k` on a Mac, so it answers both and a binding written `Ctrl+K` is the
 * same binding as one written `Mod+K` — which is exactly the shape X-A4 D1 had. Collisions are
 * compared over these rather than over `canonicalKeys`, whose job is to spell one chord one way.
 */
function strokesOf(spec: string): string[] {
	const chord = parseChord(spec)
	const tail = [chord.alt ? "alt" : "", chord.shift ? "shift" : "", positionOf(chord)].filter(
		(part) => part !== "",
	)
	const prefixes = chord.mod
		? [["ctrl"], ["meta"]]
		: [[chord.ctrl ? "ctrl" : "", chord.meta ? "meta" : ""].filter((part) => part !== "")]
	return prefixes.map((prefix) => [...prefix, ...tail].join("+"))
}

/** True on a platform where "Mod" means Cmd. Read once: it cannot change while the tab lives. */
const isMac = /mac|iphone|ipad/i.test(typeof navigator === "undefined" ? "" : navigator.userAgent)

/** Whether this machine is a Mac, for a table whose keys differ there (`core/docs/keys.ts`). */
export const IS_MAC = isMac

/**
 * What "Mod" is called on the keyboard in front of the reader. Every surface that prints a chord —
 * the sheet, a menu hint, a tooltip — uses this one word, so the product never writes "Ctrl/Cmd" in
 * one row and "Ctrl" in the next and leaves the difference to be guessed (X-A3 P14).
 */
export const MOD_KEY = isMac ? "Cmd" : "Ctrl"

/**
 * A binding with no Ctrl/Alt/Meta and a one-character key ("?", "j") is typed text everywhere a
 * person can type; `find` refuses it while a text entry has focus.
 */
function isBareCharacter(chord: Chord): boolean {
	const oneKey = chord.key.length === 1 || /^key[a-z]$|^digit[0-9]$/.test(chord.code)
	return !chord.ctrl && !chord.meta && !chord.alt && !chord.mod && oneKey
}

/**
 * True while the stroke is AltGr typing a character, never a chord (DV S-13-D10). Two tests, for the
 * two ways browsers report AltGr. Where it is a modifier of its own beside Alt (Linux, Firefox), the
 * AltGraph state says so. Windows reports AltGr as Ctrl+Alt and reads a physical Ctrl+Alt as AltGr
 * too, AltGraph state included, so there the state cannot tell a chord from a character; the
 * character can: a Ctrl+Alt stroke without Shift whose key is other than the one on the key's own
 * cap is AltGr typing (German Ctrl+Alt+2 types `²`, Ctrl+Alt+0 `}`, Ctrl+Alt+M `µ`), and one whose
 * key is the cap's own is a chord (Ctrl+Alt+1 on a layout that puts nothing there). Shift is left
 * out of the second test because the chords' Shift aliases (`Mod+Alt+Shift+2`) exist for exactly
 * that keyboard. A Mac has no AltGr: Option's characters there are why its letters fall back to the
 * position.
 */
function isAltGraph(stroke: KeyStroke, mac: boolean): boolean {
	// Some browsers on a Mac report Option as the AltGraph state; Option is a chord's Alt there.
	if (mac) return false
	const altGraph = stroke.altGraph === true || stroke.getModifierState?.("AltGraph") === true
	if (altGraph && !stroke.ctrlKey) return true
	if (!stroke.ctrlKey || !stroke.altKey || stroke.metaKey || stroke.shiftKey) return false
	if ([...stroke.key].length !== 1) return false
	const own = /^Key([A-Z])$|^Digit([0-9])$/.exec(stroke.code ?? "")
	const cap = own ? (own[1] ?? own[2] ?? "").toLowerCase() : ""
	return stroke.key.toLowerCase() !== cap
}

function keyMatches(chord: Chord, stroke: KeyStroke): boolean {
	const code = (stroke.code ?? "").toLowerCase()
	if (chord.code !== "") return code === chord.code
	const key = stroke.key.toLowerCase()
	if (/^[a-z]$/.test(chord.key)) {
		// The printed letter, or the position where the layout prints no Latin letter (D9).
		return /^[a-z]$/.test(key) ? key === chord.key : code === `key${chord.key}`
	}
	if (/^[0-9]$/.test(chord.key))
		return code === `digit${chord.key}` || (code === "" && key === chord.key)
	return key === chord.key
}

function chordMatches(chord: Chord, stroke: KeyStroke, mac = isMac): boolean {
	const wantCtrl = chord.ctrl || (chord.mod && !mac)
	const wantMeta = chord.meta || (chord.mod && mac)
	if (stroke.ctrlKey !== wantCtrl || stroke.metaKey !== wantMeta || stroke.altKey !== chord.alt) {
		return false
	}
	if (chord.alt && isAltGraph(stroke, mac)) return false
	// Shift is not compared for a punctuation character: "?" *is* Shift+7 on a German keyboard, and
	// a binding that named the character would never fire if the modifier had to be absent too.
	const punctuation = chord.key.length === 1 && !/[a-z0-9]/.test(chord.key)
	if (!punctuation && stroke.shiftKey !== chord.shift) return false
	if (punctuation && chord.shift && !stroke.shiftKey) return false
	return keyMatches(chord, stroke)
}

/** True while the focused element takes typed text, in which case a bare binding stays silent. */
export function isTextEntry(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) return false
	const tag = target.tagName.toLowerCase()
	if (tag === "textarea" || tag === "select") return true
	if (tag === "input") {
		const type = (target as HTMLInputElement).type
		return type !== "checkbox" && type !== "radio" && type !== "button" && type !== "submit"
	}
	return target.closest("[contenteditable='true']") !== null
}

/**
 * Whether one key spec answers one stroke — for a surface that binds a registry-spelled chord
 * outside the registry: the composer's send menu, whose entries carry keys of their own while the
 * box has the focus (`core/composer/ComposerFrame.tsx`). A bare character never matches inside a
 * text field, for the same reason `find` refuses it there.
 */
export function matchesKeys(
	spec: string,
	stroke: KeyStroke,
	inTextEntry = false,
	mac = isMac,
): boolean {
	const chord = parseChord(spec)
	if (inTextEntry && isBareCharacter(chord)) return false
	return chordMatches(chord, stroke, mac)
}

/**
 * How a key is written on the keycap in front of the reader, where the DOM's own name for it is not
 * that: `ArrowDown` is an arrow, and printing the identifier would ask a person to translate it.
 * Everything not in here keeps its name with a capital first letter.
 */
const KEY_NAMES: Record<string, string> = {
	arrowdown: "\u2193",
	arrowup: "\u2191",
	arrowleft: "\u2190",
	arrowright: "\u2192",
	escape: "Esc",
	" ": "Space",
}

/**
 * "Mod+Shift+Enter" as a person reads it on this machine, one entry per key: `["Ctrl", "Shift",
 * "Enter"]`, or `Cmd` on a Mac. The keyboard sheet draws one `<kbd>` per entry; a menu row joins them
 * with "+" (`printKeys`). One spelling for every surface that prints a chord (X-A3 P14).
 */
export function keyParts(spec: string): string[] {
	return canonicalKeys(spec)
		.split("+")
		.map((part) => {
			if (part === "mod") return MOD_KEY
			const code = /^\[(.+)\]$/.exec(part)?.[1]
			if (code !== undefined) return capLabel(code)
			return KEY_NAMES[part] ?? part.replace(/^./, (c) => c.toUpperCase())
		})
}

/**
 * The reader's own keycaps by position, where the browser names them (`navigator.keyboard`, which
 * Chromium has and Firefox and Safari lack): on a German keyboard `[BracketRight]` is the `+` key.
 * Asked once, when something that prints code chords opens; until the answer comes, and where it
 * never does, the American labels stand.
 */
let layoutLabels: ReadonlyMap<string, string> | null = null

interface LayoutKeyboard {
	getLayoutMap(): Promise<ReadonlyMap<string, string>>
}

/** Asks the browser for the keyboard's own labels, once; harmless where it cannot answer. */
export function learnKeyLabels(): void {
	if (layoutLabels !== null || typeof navigator === "undefined") return
	const keyboard = (navigator as Navigator & { keyboard?: LayoutKeyboard }).keyboard
	if (keyboard === undefined) return
	layoutLabels = new Map()
	keyboard.getLayoutMap().then(
		(map) => {
			layoutLabels = new Map([...map].map(([code, label]) => [code.toLowerCase(), label]))
		},
		() => undefined,
	)
}

/** How a position is printed: the keyboard's own label, else the American one ("KeyH" is H). */
function capLabel(code: string): string {
	const own = layoutLabels?.get(code)
	if (own !== undefined && own.trim() !== "") return own.toUpperCase()
	return usLabel(code)
}

/** The American label of a position: "keyh" is H, "digit7" 7, "bracketright" ]. */
function usLabel(code: string): string {
	const letter = /^key([a-z])$/.exec(code)?.[1]
	if (letter !== undefined) return letter.toUpperCase()
	const digit = /^digit([0-9])$/.exec(code)?.[1]
	if (digit !== undefined) return digit
	return US_CHARACTERS[code] ?? code.replace(/^./, (c) => c.toUpperCase())
}

/** "Ctrl+Shift+Enter" — a chord printed at the end of a menu row or in a tooltip. */
export function printKeys(spec: string): string {
	return keyParts(spec).join("+")
}

/**
 * The same chord in the grammar `aria-keyshortcuts` defines: `Control`, `Meta`, `Alt` and `Shift`,
 * and a "Mod" chord as its two alternatives separated by a space, because assistive technology
 * cannot know which of the two this machine means.
 */
export function ariaKeys(spec: string): string {
	return strokesOf(spec)
		.map((stroke) =>
			stroke
				.split("+")
				.map((part) => ARIA_NAMES[part] ?? ariaKeyName(part))
				.join("+"),
		)
		.join(" ")
}

/**
 * A key as `aria-keyshortcuts` writes it: a position by the character it carries on the American
 * layout ("]", "7", "H"), which is the one spelling assistive technology can be told in advance.
 */
function ariaKeyName(part: string): string {
	if (/^key[a-z]$|^digit[0-9]$/.test(part) || US_CHARACTERS[part] !== undefined)
		return usLabel(part)
	return part.replace(/^./, (c) => c.toUpperCase())
}

/**
 * The modifiers and the two-word keys as `aria-keyshortcuts` names them; every other key is its own
 * name with a capital first letter ("Enter", "K").
 */
const ARIA_NAMES: Record<string, string> = {
	ctrl: "Control",
	meta: "Meta",
	alt: "Alt",
	shift: "Shift",
	arrowup: "ArrowUp",
	arrowdown: "ArrowDown",
	arrowleft: "ArrowLeft",
	arrowright: "ArrowRight",
	pageup: "PageUp",
	pagedown: "PageDown",
}

/** What core's own bindings do; the shell hands its own handlers in (`Shell.tsx`). */
export interface CoreShortcutActions {
	openQuickSwitcher(): void
	openShortcutSheet(): void
	goToSearch(): void
	goToActivity(): void
	/** F6 and Shift+F6: the next or the previous landmark of the frame. */
	cycleRegions(step: 1 | -1): void
	/** Alt+←: one step back through the router's history — the conversation the reader came from. */
	goBack(): void
}

/**
 * Every binding core registers, as data rather than as a list inside a component — which is what
 * lets one test ask the whole application whether two of its chords collide (`shortcuts.test.ts`,
 * `modules/chat/shortcuts.test.ts`). Activity's are part of it: the feed is a core page with a
 * keyboard of its own, and a module's bindings arrive through the `shortcuts` slot instead.
 */
export function coreShortcuts(actions: CoreShortcutActions): ShortcutDef[] {
	return [
		{
			id: "quickSwitcher",
			keys: ["Mod+K"],
			scope: "global",
			labelKey: "shell.shortcut.quickSwitcher",
			run: () => actions.openQuickSwitcher(),
		},
		{
			id: "search",
			keys: ["Mod+Shift+F"],
			scope: "global",
			labelKey: "shell.shortcut.search",
			run: () => actions.goToSearch(),
		},
		{
			id: "activity",
			keys: ["Ctrl+Shift+A"],
			scope: "global",
			labelKey: "shell.shortcut.activity",
			run: () => actions.goToActivity(),
		},
		{
			id: "sheet",
			keys: ["Ctrl+/", "?"],
			scope: "global",
			labelKey: "shell.shortcut.sheet",
			run: () => actions.openShortcutSheet(),
		},
		{
			// The browser's own back chord, answered here so that it works the same in a standalone
			// window and in a tab, and so that the sheet documents it beside the other moves between
			// conversations (VISUAL.md §8 U15).
			id: "back",
			keys: ["Alt+ArrowLeft"],
			scope: "global",
			labelKey: "shell.shortcut.back",
			run: () => actions.goBack(),
		},
		{
			id: "regionNext",
			keys: ["F6"],
			scope: "global",
			labelKey: "shell.shortcut.regionNext",
			run: () => actions.cycleRegions(1),
		},
		{
			id: "regionPrev",
			keys: ["Shift+F6"],
			scope: "global",
			labelKey: "shell.shortcut.regionPrev",
			run: () => actions.cycleRegions(-1),
		},
		...activityShortcuts,
		...documentShortcuts(isMac),
	]
}

/**
 * A binding that says out loud what it did (X-B3 A2). Three chords change something a sighted reader
 * can see and a screen-reader reader cannot: a scroll position, a read cursor, a row that stopped
 * being bold. The binding names one key and the provider speaks it through the one announcer
 * (`Announcer.tsx`), so the whole keyboard becomes audible at the cost of one line per binding.
 *
 * Two rules decide whether a binding carries one. A binding that shows a **toast** does not: a toast
 * is a `role="status"` or `role="alert"` of its own and the two together speak the same sentence
 * twice. And a binding whose sentence depends on what it did — *read*, or *unread again* — announces
 * from its own handler, where the outcome is known; `announceKey` is for the effect that is always
 * the same one.
 *
 * The field is declared here rather than on `ShortcutDef` (`core/modules/registry.ts`) until that
 * file's owner adds it; `announceKeyOf` reads it either way, which is what lets a module's bindings
 * carry one the moment the line lands and nothing break before it does.
 */
export interface AnnouncingShortcut extends ShortcutDef {
	announceKey?: string
}

/** The key a binding announces itself with, or undefined for the ones that speak for themselves. */
export function announceKeyOf(def: ShortcutDef): string | undefined {
	if (!("announceKey" in def)) return undefined
	return typeof def.announceKey === "string" && def.announceKey !== "" ? def.announceKey : undefined
}

/**
 * Runs one binding and says what it did, in that order — the sentence is about something that has
 * already happened, and a binding that threw says nothing at all. Exactly one utterance per press:
 * the announcer's own rate limit coalesces a burst, and a second call here would be a second
 * sentence about one keystroke.
 */
export function runShortcut(def: ShortcutDef, announce: (key: string) => void): void {
	def.run()
	const key = announceKeyOf(def)
	if (key !== undefined) announce(key)
}

export interface ShortcutRegistry {
	all(): readonly ShortcutDef[]
	/** The bindings of one scope, for the sheet and for a menu hint. */
	forScope(scope: ShortcutScope): readonly ShortcutDef[]
	/** The keys one binding is on after overrides, for rendering it. */
	keysOf(id: string): readonly string[]
	/** The binding a stroke fires, or undefined. `scopes` is the stack, innermost last. */
	find(
		stroke: KeyStroke,
		scopes: readonly ShortcutScope[],
		inTextEntry: boolean,
	): ShortcutDef | undefined
	/**
	 * Pairs of ids that answer the same stroke while their scopes can be open together on one of
	 * `stacks` — a programming error.
	 */
	conflicts(stacks: ScopeStacks): string[]
}

/**
 * Builds the registry from the definitions and the reader's own remapping (`tb:shortcuts`). An
 * override only applies to a binding that allows it; the rest keep the keys they document.
 */
export function createShortcutRegistry(
	defs: readonly ShortcutDef[],
	overrides: Record<string, string[]> = {},
): ShortcutRegistry {
	const keys = new Map<string, string[]>()
	for (const def of defs) {
		const override = def.remappable === true ? overrides[def.id] : undefined
		keys.set(def.id, (override ?? def.keys).map(canonicalKeys))
	}

	function chordsOf(def: ShortcutDef): Chord[] {
		return (keys.get(def.id) ?? []).map(parseChord)
	}

	return {
		all: () => defs,
		forScope: (scope) => defs.filter((def) => def.scope === scope),
		keysOf: (id) => keys.get(id) ?? [],
		find(stroke, scopes, inTextEntry) {
			// Innermost scope first: the composer's "↑" wins over anything global on the same key.
			for (let index = scopes.length - 1; index >= 0; index--) {
				const scope = scopes[index]
				for (const def of defs) {
					if (def.scope !== scope) continue
					for (const chord of chordsOf(def)) {
						if (inTextEntry && isBareCharacter(chord)) continue
						if (chordMatches(chord, stroke)) return def
					}
				}
			}
			return undefined
		},
		conflicts(stacks) {
			// Two bindings clash when one stroke can reach both, which is exactly when their scopes can
			// be on the stack together (`scopeStacks`). `find` would answer the innermost of them and
			// the other would simply never fire — a binding documented in the sheet that does nothing.
			const bySpec = new Map<string, { id: string; scope: ShortcutScope }[]>()
			for (const def of defs) {
				for (const spec of keys.get(def.id) ?? []) {
					for (const stroke of strokesOf(spec)) {
						const holders = bySpec.get(stroke) ?? []
						holders.push({ id: def.id, scope: def.scope })
						bySpec.set(stroke, holders)
					}
				}
			}
			// A pair, not a stroke: "Mod+K" answers two strokes on the two platforms, and one pair of
			// bindings is one programming error however many ways round it can be pressed.
			const found = new Set<string>()
			for (const holders of bySpec.values()) {
				for (let a = 0; a < holders.length; a++) {
					for (let b = a + 1; b < holders.length; b++) {
						const one = holders[a]!
						const other = holders[b]!
						if (scopesOverlap(one.scope, other.scope, stacks)) found.add(`${one.id}+${other.id}`)
					}
				}
			}
			return [...found]
		},
	}
}
